/**
 * Google Analytics 4 Event Tracking Utility
 * 
 * This utility provides functions to track custom events in Google Analytics 4.
 * Make sure gtag is loaded via the script in index.html before using these functions.
 */

/**
 * Track a page view (GA4 SPA pattern).
 *
 * The trick that fixes per-page reports without breaking session tracking:
 * fire `gtag('event', 'page_view', …)` and include ALL THREE of `page_location`,
 * `page_path`, and `page_title`. The `page_location` is the critical bit —
 * without it, GA4 falls back to whatever URL was cached at `gtag('config', …)`
 * load time, which is why every route was being attributed to the landing URL.
 *
 * DO NOT re-fire `gtag('config', GA_MEASUREMENT_ID, …)` on route changes —
 * that re-initializes the GA4 property, resets sessions, and drops source/
 * referrer attribution. Pair this with `send_page_view: false` in index.html
 * so the manual event below is the only pageview that fires.
 *
 * @param {string} path  - The page path (e.g. "/about?ref=foo")
 * @param {string} title - The page title (defaults to current document.title)
 */
export const trackPageView = (path, title) => {
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.href,
  });
};

/**
 * Track quote submission
 * @param {object} quoteData - Quote details
 */
export const trackQuoteSubmission = (quoteData) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'quote_submitted', {
      event_category: 'Engagement',
      event_label: 'Instant Quote',
      material: quoteData.material || 'unknown',
      quality: quoteData.quality || 'unknown',
      estimated_price: quoteData.estimated_price || 0,
    });
    console.log('✅ GA Event Sent: quote_submitted', quoteData);
  } else {
    console.warn('⚠️ Google Analytics (gtag) not loaded - event not sent');
  }
};

/**
 * Track order creation
 * @param {object} orderData - Order details
 */
export const trackOrderCreation = (orderData) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'purchase', {
      event_category: 'Ecommerce',
      transaction_id: orderData.order_id || 'unknown',
      value: orderData.total_price || 0,
      currency: 'USD',
      items: [{
        item_id: orderData.quote_id || orderData.product_id || 'unknown',
        item_name: orderData.item_name || 'Print Order',
        item_category: 'Print Service',
        price: orderData.total_price || 0,
        quantity: 1,
      }],
    });
  }
};

/**
 * Track add to cart action
 * @param {object} item - Item details
 */
export const trackAddToCart = (item) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'add_to_cart', {
      event_category: 'Ecommerce',
      currency: 'USD',
      value: item.price || 0,
      items: [{
        item_id: item.id || 'unknown',
        item_name: item.name || item.title || 'Unknown Item',
        item_category: item.type || 'Product',
        price: item.price || 0,
        quantity: 1,
      }],
    });
  }
};

/**
 * Track marketplace design view
 * @param {object} design - Design details
 */
export const trackDesignView = (design) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'view_item', {
      event_category: 'Marketplace',
      event_label: design.title || 'Unknown Design',
      items: [{
        item_id: design.id || 'unknown',
        item_name: design.title || 'Unknown Design',
        item_category: design.category || 'Design',
        price: design.personal_price || 0,
      }],
    });
  }
};

/**
 * Track contact form submission
 * @param {string} formType - Type of contact form
 */
export const trackContactFormSubmission = (formType) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'form_submit', {
      event_category: 'Engagement',
      event_label: formType || 'Contact Form',
    });
  }
};

/**
 * Track service request
 * @param {string} serviceType - Type of service requested
 */
export const trackServiceRequest = (serviceType) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'service_request', {
      event_category: 'Engagement',
      event_label: serviceType || 'Unknown Service',
    });
  }
};

/**
 * Track user authentication
 * @param {string} method - Authentication method (google, email, etc.)
 */
export const trackUserAuth = (method) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'login', {
      method: method || 'unknown',
    });
  }
};

/**
 * Track designer application
 */
export const trackDesignerApplication = () => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'designer_application', {
      event_category: 'Engagement',
      event_label: 'Designer Application Submitted',
    });
  }
};

/**
 * Track design upload
 * @param {object} designData - Design details
 */
export const trackDesignUpload = (designData) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'design_upload', {
      event_category: 'Marketplace',
      event_label: designData.title || 'Unknown Design',
      design_category: designData.category || 'unknown',
    });
  }
};

/**
 * Track custom event
 * @param {string} eventName - Name of the event
 * @param {object} params - Event parameters
 */
export const trackCustomEvent = (eventName, params = {}) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
};

// ============================================================================
// META PIXEL (Facebook Pixel) — injected dynamically when an admin sets the ID
// in /admin → Settings → Marketing & Tracking. The script tag itself is added
// once by `loadMetaPixel(pixelId)` from App.js. After it loads, `window.fbq`
// becomes available and these helpers fire standard events.
// ============================================================================

/**
 * Inject the Meta Pixel base script. Idempotent — safe to call repeatedly with
 * the same ID; calls with a different ID are ignored to avoid duplicate init.
 * @param {string} pixelId - Numeric Meta Pixel ID
 */
export const loadMetaPixel = (pixelId) => {
  if (typeof window === 'undefined' || !pixelId) return;
  // Already initialized for this exact ID? skip
  if (window.__metaPixelLoadedFor === pixelId) return;
  if (window.__metaPixelLoadedFor && window.__metaPixelLoadedFor !== pixelId) {
    // Different pixel requested — Meta supports init() of additional IDs but for
    // simplicity we ignore re-inits to avoid double-counting.
    return;
  }

  // Standard Meta Pixel base code (https://developers.facebook.com/docs/meta-pixel/get-started)
  /* eslint-disable */
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
  n.queue=[];t=b.createElement(e);t.async=!0;
  t.src=v;s=b.getElementsByTagName(e)[0];
  s.parentNode.insertBefore(t,s)}(window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  if (typeof window.fbq === 'function') {
    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');
    window.__metaPixelLoadedFor = pixelId;
  }
};

/** Fire a Meta Pixel PageView (used on SPA route changes). */
export const trackPixelPageView = () => {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('track', 'PageView');
  }
};

/** Fire Meta Pixel Lead event (quote submitted, contact form, etc.). */
export const trackPixelLead = (params = {}) => {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('track', 'Lead', params);
  }
};

/** Fire Meta Pixel AddToCart event. */
export const trackPixelAddToCart = (item = {}) => {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('track', 'AddToCart', {
      content_ids: [item.id || item.quote_id || 'unknown'],
      content_name: item.name || 'Cart Item',
      content_type: 'product',
      value: Number(item.price) || 0,
      currency: item.currency || 'CAD',
    });
  }
};

/** Fire Meta Pixel Purchase event on successful checkout. */
export const trackPixelPurchase = (order = {}) => {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('track', 'Purchase', {
      value: Number(order.value) || 0,
      currency: order.currency || 'CAD',
      content_ids: order.content_ids || [],
      content_type: 'product',
    });
  }
};
